import { ArdoqIcon } from '@ardoq/data-model';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  GraphItem,
  NodeExportSupport,
  type YGraphState,
} from '@ardoq/graph';
import {
  Constructor,
  GraphComponent,
  INode,
  IRenderContext,
  Rect,
} from '@ardoq/yfiles';
import { componentInterface } from '@ardoq/component-interface';
import { canvasResolvedImages } from 'tabview/canvasRendering/canvasResolvedImages';
import { LeafNodeVisualProperties } from './types';
import { MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING as PADDING } from '../consts';
import { ComponentNode } from 'graph/ComponentNode';
import { Edge } from 'graph/edge';
import { colors } from '@ardoq/design-tokens';

/**
 * Calculates the layout for an icon element
 */
export function calculateIconLayout(
  centerX: number,
  centerY: number,
  size: number,
  _icon: ArdoqIcon
): Rect {
  // For both FontAwesome and SVG icons, they are rendered centered
  // with the specified size
  const x = calculateCenterX(centerX, size);
  const y = calculateCenterY(centerY, size);
  return new Rect(x, y, size, size);
}

/**
 * Calculates the layout for an image element inside a circle
 */
export function calculateImageInCircleLayout(
  centerX: number,
  centerY: number,
  circleRadius: number,
  imageHref: string
): Rect {
  const img = canvasResolvedImages.get(imageHref);

  if (img?.complete) {
    const { naturalWidth, naturalHeight } = img;
    const { width, height } = calculateImageDimensions(
      circleRadius,
      naturalWidth,
      naturalHeight
    );

    const x = calculateCenterX(centerX, width);
    const y = calculateCenterY(centerY, height);

    return new Rect(x, y, width, height);
  }

  // Fallback if image is not loaded - assume square image filling the circle
  const size = circleRadius * 2;
  const x = calculateCenterX(centerX, size);
  const y = calculateCenterY(centerY, size);
  return new Rect(x, y, size, size);
}

/**
 * Calculates the x position for a centered element
 */
export function calculateCenterX(centerX: number, width: number): number {
  return centerX - width / 2;
}

/**
 * Calculates the y position for a centered element
 */
export function calculateCenterY(centerY: number, height: number): number {
  return centerY - height / 2;
}

/**
 * Calculates dimensions for an image inside a circle based on aspect ratio
 */
export function calculateImageDimensions(
  circleRadius: number,
  naturalWidth: number,
  naturalHeight: number
): { width: number; height: number } {
  const isTall = naturalHeight > naturalWidth;
  const aspectRatio = naturalWidth / naturalHeight;
  const maxSize = circleRadius * 2; // Diameter of the circle

  const imageWidth = isTall ? maxSize * aspectRatio : maxSize;
  const imageHeight = isTall ? maxSize : maxSize / aspectRatio;

  return { width: imageWidth, height: imageHeight };
}

export function buildLeafProps(
  node: INode,
  ctx: IRenderContext,
  variant: Partial<LeafNodeVisualProperties>
): LeafNodeVisualProperties {
  const { x, y, height } = node.layout;

  const graphNode: ComponentGraphNode = node.tag;

  const { id, isTransparentized } = graphNode;

  const graphComponent = ctx.canvasComponent as GraphComponent;
  const gs = graphComponent.graph.tag as YGraphState;
  const shapeY = gs.leafNodeLayout.get(id)?.shapeY ?? PADDING;
  const isContext = graphNode.getIsContext();
  const isSelected = graphComponent.selection.nodes.includes(node);
  const conditionalFormattingState = graphNode.getConditionalFormattingState();

  return {
    x,
    y,
    height,
    shapeY,
    isTransparentized,
    isContext,
    isSelected,
    conditionalFormattingState,
    ...variant,
  } as LeafNodeVisualProperties;
}

/**
 * This is slightly faster than instanceof operation in cases where we have mixed classes
 */
export const isPureGraphNodeOrGroup = (
  graphItem: GraphItem | ComponentNode | Edge
): graphItem is ComponentGraphGroup | ComponentGraphNode =>
  'getCssColorAttributes' in graphItem && 'getRepresentationData' in graphItem;

export const getColor = (
  node: INode,
  useAsBackgroundStyle: boolean = false
): string => {
  const tag = node.tag;

  if (isPureGraphNodeOrGroup(tag)) {
    return tag.getCssColorAttributes()?.fill ?? colors.black950;
  }

  // fall back to modelInterfaces if no class data

  return (
    componentInterface.getComponentDisplayColorAsSVGAttributes(
      tag.modelId || '',
      {
        useAsBackgroundStyle,
      }
    )?.fill || 'transparent'
  );
};

export const createNodeExportSupportLookup = (
  getLayoutFn: (context: IRenderContext, node: INode) => Rect
) => {
  return (_node: INode, type: Constructor): NodeExportSupport | unknown => {
    if (type === NodeExportSupport) {
      return {
        getSubShapeLayout: (gc: GraphComponent, n: INode): Rect | null => {
          const rc: IRenderContext = gc.createRenderContext();
          return getLayoutFn(rc, n);
        },
      } satisfies NodeExportSupport;
    }
    return undefined;
  };
};
